import React, { useState, useMemo, useEffect } from "react";
import { X } from "lucide-react";

import { useAppSelector } from "../../../app/store";
import { selectFormConfigById } from "../Logic/formConfigsSlice";

import { FieldConfig, FormFieldType, FormType } from "../../../api/formConfigs";
import DynamicFormField from "./DynamicFormField";

const FULL_WIDTH_FIELD_TYPES = new Set<FormFieldType>([
  FormFieldType.TEXT_AREA,
  FormFieldType.MULTISELECT,
  FormFieldType.RADIOBUTTON,
]);

const isFullWidthField = (field: FieldConfig): boolean =>
  FULL_WIDTH_FIELD_TYPES.has(field.field_type);

interface ConfigBasedFormModalProps {
  title: string;
  formType: FormType;
  values?: Record<string, any>;
  onSave(details: Record<string, any>): void;
  onClose(): void;
}

const ConfigBasedFormModal: React.FC<ConfigBasedFormModalProps> = ({
  title,
  formType,
  values,
  onSave,
  onClose,
}) => {
  const formConfigData = useAppSelector((state) =>
    selectFormConfigById(state, formType)
  );

  const [formValues, setFormValues] = useState<Record<string, any>>(
    values ?? {}
  );

  useEffect(() => {
    setFormValues(values ?? {});
  }, [values]);

  const handleFieldChange = (key: string, value: unknown) => {
    setFormValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const orderedFields = useMemo(() => {
    if (!formConfigData?.configs?.fields) return [];

    const fields = formConfigData.configs.fields;
    const leaderKeys = formConfigData.configs.leader_field_keys ?? [];

    const leaderFields = fields.filter((field) =>
      leaderKeys.includes(field.key)
    );
    const remainingFields = fields.filter(
      (field) => !leaderKeys.includes(field.key)
    );

    return [...leaderFields, ...remainingFields];
  }, [formConfigData]);

  const { layoutFields, fieldSpanClasses } = useMemo(() => {
    const halfFields = orderedFields.filter((field) => !isFullWidthField(field));
    const fullFields = orderedFields.filter((field) => isFullWidthField(field));

    const spans: Record<string, string> = {};
    halfFields.forEach((field) => {
      spans[field.key] = "col-span-1";
    });
    fullFields.forEach((field) => {
      spans[field.key] = "col-span-2";
    });

    if (halfFields.length % 2 === 1) {
      spans[halfFields[halfFields.length - 1].key] = "col-span-2";
    }

    return {
      layoutFields: [...halfFields, ...fullFields],
      fieldSpanClasses: spans,
    };
  }, [orderedFields]);

  const isSaveEnabled = useMemo(() => {
    if (!formConfigData?.configs?.fields?.length) return false;

    return formConfigData.configs.fields.every((field) => {
      if (!field.is_mandatory) return true;

      const value = formValues[field.key];
      
      if (Array.isArray(value)) {
        return value.length > 0;
      }

      return value !== undefined && value !== null && value !== "";
    });
  }, [formConfigData, formValues]);

  const handleSave = () => {
    if (isSaveEnabled) {
      onSave(formValues);
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-[rgba(10,10,10,0.20)]"
      onClick={onClose}
    >
      <div
        className="relative w-[608px] max-h-[90vh] flex flex-col overflow-hidden rounded-[10px] border border-[#E5E5E5] bg-white p-6 shadow-[0_10px_15px_-3px_rgba(0,0,0,0.1),0_4px_6px_-4px_rgba(0,0,0,0.1)] gap-6"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-6 flex h-4 w-4 items-center justify-center rounded-full focus:outline-none"
          aria-label="Close modal"
        >
          <X size={16} className="text-[#0A0A0A] opacity-70" strokeWidth={1.33} />
        </button>

        <div className="flex flex-col gap-1.5 w-full">
          <h2 className="text-[18px] font-semibold leading-none text-[#0A0A0A]">
            {title}
          </h2>
        </div>

        <div className="overflow-y-auto max-h-[65vh] pr-1 w-full">
          {layoutFields.length === 0 && (
            <div className="flex items-center justify-center py-10 text-sm text-[#737373]">
              No fields to configure for this document type.
            </div>
          )}

          <div className="grid grid-cols-2 items-start gap-x-4 gap-y-4 w-full">
            {layoutFields.map((field) => {
              const spanClass = fieldSpanClasses[field.key] || "col-span-1";

              return (
                <div key={field.key} className={`flex flex-col gap-2 ${spanClass}`}>
                  <label className="text-sm font-medium text-[#0A0A0A]">
                    {field.label}
                    {field.is_mandatory && (
                      <span className="text-[#DC2626] font-medium text-sm ml-0.5 leading-none">*</span>
                    )}
                  </label>

                  <DynamicFormField
                    field={field}
                    value={formValues[field.key]}
                    onChange={(value) => handleFieldChange(field.key, value)}
                  />
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end gap-3 w-full">
          <button
            type="button"
            onClick={onClose}
            className="h-9 px-4 py-2 rounded-[8px] border border-[#E5E5E5] bg-white text-sm font-medium text-[#0A0A0A] hover:bg-gray-50 focus:outline-none flex items-center justify-center gap-2 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!isSaveEnabled}
            style={{
              background: isSaveEnabled
                ? "linear-gradient(180deg, #788AF7 0%, #3551F3 100%)"
                : "#9CA3AF",
            }}
            className={`h-9 px-4 py-2 rounded-[8px] text-sm font-medium text-white focus:outline-none flex items-center justify-center gap-2 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] transition-opacity ${
              isSaveEnabled ? "hover:opacity-95" : "cursor-not-allowed opacity-60"
            }`}
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfigBasedFormModal;
